import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, AlertCircle, Wifi, ServerCrash,
  CheckCircle, X, User, Mail, Phone, Building2, Globe,
  Briefcase, Clock, HelpCircle, MessageSquare, Sparkles,
  Bot, LayoutTemplate, PhoneCall, Workflow, Check,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { retryOperation } from '../lib/utils';

const MAKE_WEBHOOK_URL = import.meta.env.VITE_MAKE_WEBHOOK_URL;

const formSchema = z.object({
  fullName: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string()
    .transform(val => val.replace(/\D/g, ''))
    .refine(val => val.length >= 10, 'Phone number must be at least 10 digits'),
  service: z.enum(['all', 'chatbot', 'website', 'phone-agent', 'business-automations']),
  company: z.string().optional(),
  role: z.string().optional(),
  website: z.string().optional(),
  projectTimeline: z.string().optional(),
  referralSource: z.string().optional(),
  problems: z.string().min(1, 'Please describe your problems'),
  additionalInfo: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

const SERVICES = [
  { value: 'all', label: 'All Services', icon: Sparkles, desc: 'Full-suite AI transformation' },
  { value: 'chatbot', label: 'AI Chat Bot', icon: Bot, desc: 'Intelligent conversational agents' },
  { value: 'website', label: 'Website Builds', icon: LayoutTemplate, desc: 'Custom AI-powered websites' },
  { value: 'phone-agent', label: 'AI Phone Agent', icon: PhoneCall, desc: '24/7 automated call handling' },
  { value: 'business-automations', label: 'Business Automations', icon: Workflow, desc: 'Streamline your operations' },
];

const ROLES = [
  { value: '', label: 'Select your role' },
  { value: 'founder', label: 'Founder/Co-founder' },
  { value: 'marketing', label: 'Marketing Lead' },
  { value: 'product', label: 'Product Lead' },
  { value: 'other', label: 'Other' },
];

const TIMELINES = [
  { value: '', label: 'Select timeline' },
  { value: 'urgent', label: 'Urgent (in 7 days)' },
  { value: 'standard', label: 'Standard (14 days)' },
  { value: 'flexible', label: 'Flexible (21 days)' },
  { value: 'other', label: 'Other' },
];

const SOURCES = [
  { value: '', label: 'Select source' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'google', label: 'Google' },
  { value: 'referral', label: 'Referral' },
  { value: 'other', label: 'Other' },
];

const STEPS = [
  { title: 'About You', icon: User },
  { title: 'Your Business', icon: Building2 },
  { title: 'Project Details', icon: MessageSquare },
];

const selectArrowStyle = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23D4AF37'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 1rem center',
  backgroundSize: '1.5em',
  paddingRight: '2.5rem',
};

const inputClass =
  'w-full bg-black/30 border border-gold-500/20 rounded-xl px-4 py-3.5 text-white placeholder-gold-200/40 focus:outline-none focus:border-gold-500/60 focus:ring-2 focus:ring-gold-500/20 transition-all duration-300';

const selectClass =
  'w-full bg-black border border-gold-500/20 rounded-xl px-4 py-3.5 text-gold-200 focus:outline-none focus:border-gold-500/60 focus:ring-2 focus:ring-gold-500/20 appearance-none cursor-pointer hover:border-gold-500/40 transition-all duration-300';

const ContactForm: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!MAKE_WEBHOOK_URL) {
      console.error('Make webhook URL is not configured');
    } else {
      try {
        new URL(MAKE_WEBHOOK_URL);
      } catch (e) {
        console.error('Invalid webhook URL format:', e);
      }
    }
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    service: 'all',
    company: '',
    role: '',
    website: '',
    projectTimeline: '',
    referralSource: '',
    problems: '',
    additionalInfo: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<{
    type: 'network' | 'server' | 'validation' | 'unknown' | 'configuration';
    message: string;
    details?: string;
  } | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);

  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      const formattedValue = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    if (submitError) {
      setSubmitError(null);
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 0) {
      const nameResult = formSchema.shape.fullName.safeParse(formData.fullName);
      if (!nameResult.success) newErrors.fullName = nameResult.error.errors[0].message;

      const emailResult = formSchema.shape.email.safeParse(formData.email);
      if (!emailResult.success) newErrors.email = emailResult.error.errors[0].message;

      const phoneResult = formSchema.shape.phone.safeParse(formData.phone);
      if (!phoneResult.success) newErrors.phone = phoneResult.error.errors[0].message;
    }

    if (step === 2) {
      const problemsResult = formSchema.shape.problems.safeParse(formData.problems);
      if (!problemsResult.success) newErrors.problems = problemsResult.error.errors[0].message;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setDirection(1);
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    setDirection(-1);
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const validateWebhookUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  const submitToSupabase = async (data: FormData) => {
    const { error } = await supabase
      .from('appointments')
      .insert([
        {
          full_name: data.fullName,
          email: data.email,
          phone: data.phone.replace(/\D/g, ''),
          service: data.service,
          company: data.company || null,
          role: data.role || null,
          website: data.website || null,
          project_timeline: data.projectTimeline || null,
          referral_source: data.referralSource || null,
          problems: data.problems,
          additional_info: data.additionalInfo || null
        }
      ]);

    if (error) {
      throw new Error(`Supabase error: ${error.message}`);
    }
  };

  const submitToMakeWebhook = async (data: FormData) => {
    if (!MAKE_WEBHOOK_URL) {
      throw new Error('configuration:Make webhook URL is not configured');
    }

    if (!validateWebhookUrl(MAKE_WEBHOOK_URL)) {
      throw new Error('configuration:Invalid webhook URL format');
    }

    try {
      const response = await fetch(MAKE_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName: data.fullName,
          email: data.email,
          phone: data.phone,
          service: data.service,
          company: data.company || '',
          role: data.role || '',
          website: data.website || '',
          projectTimeline: data.projectTimeline || '',
          referralSource: data.referralSource || '',
          problems: data.problems,
          additionalInfo: data.additionalInfo || '',
          submittedAt: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`server:HTTP error! status: ${response.status}. ${errorText}`);
      }
    } catch (error) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('network:Unable to reach the server. Please check your internet connection and try again.');
      }
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});
    setSubmitError(null);

    try {
      const validatedData = formSchema.parse(formData);

      const submissions = [
        retryOperation(() => submitToSupabase(validatedData), 3, 1000)
      ];

      if (MAKE_WEBHOOK_URL) {
        submissions.push(retryOperation(() => submitToMakeWebhook(validatedData), 3, 1000));
      }

      const results = await Promise.allSettled(submissions);

      const hasSuccess = results.some(result => result.status === 'fulfilled');

      if (!hasSuccess) {
        const errors = results
          .filter(result => result.status === 'rejected')
          .map(result => (result as PromiseRejectedResult).reason.message)
          .join(', ');
        throw new Error(`All submission methods failed: ${errors}`);
      }

      const services = MAKE_WEBHOOK_URL ? ['Supabase', 'Make Webhook'] : ['Supabase'];
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.warn(`${services[index]} submission failed:`, result.reason);
        }
      });

      setShowSuccessModal(true);

    } catch (error) {
      console.error('Form submission error:', error);

      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach(err => {
          if (err.path[0]) {
            newErrors[err.path[0].toString()] = err.message;
          }
        });
        setErrors(newErrors);
        setSubmitError({
          type: 'validation',
          message: 'Please correct the errors in the form.'
        });
      } else if (error instanceof Error) {
        const [errorType, ...messageParts] = error.message.split(':');
        const errorMessage = messageParts.join(':') || error.message;

        if (errorType === 'network') {
          setSubmitError({
            type: 'network',
            message: errorMessage,
            details: 'Please check your internet connection and try again. If the problem persists, try refreshing the page.'
          });
        } else if (errorType === 'server') {
          setSubmitError({
            type: 'server',
            message: 'Server error: Unable to process your request.',
            details: 'Our team has been notified. Please try again later or contact support if the issue persists.'
          });
        } else if (errorType === 'configuration') {
          console.error('Configuration error detail:', errorMessage);
          setSubmitError({
            type: 'configuration',
            message: 'We could not submit your request right now.',
            details: 'Please try again in a few minutes, or email us directly and we will get straight back to you.'
          });
        } else {
          setSubmitError({
            type: 'unknown',
            message: 'An unexpected error occurred.',
            details: 'Please try again or contact support if the issue persists.'
          });
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 60 : -60,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -60 : 60,
      opacity: 0,
    }),
  };

  const stepCompletion = (step: number) => {
    if (step === 0) return formData.fullName && formData.email && formData.phone.replace(/\D/g, '').length >= 10;
    if (step === 1) return !!formData.service;
    if (step === 2) return formData.problems.length > 0;
    return false;
  };

  return (
    <>
      <AnimatePresence>
        {showSuccessModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => {
              setShowSuccessModal(false);
              navigate('/', { replace: true });
              window.scrollTo(0, 0);
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-gradient-to-br from-black via-gray-900 to-black border-2 border-gold-500/50 rounded-2xl p-8 max-w-md w-full relative shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/', { replace: true });
                  window.scrollTo(0, 0);
                }}
                className="absolute top-4 right-4 text-gold-300 hover:text-gold-100 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="flex flex-col items-center text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="w-20 h-20 rounded-full bg-gold-500/20 flex items-center justify-center mb-6"
                >
                  <CheckCircle className="w-12 h-12 text-gold-400" />
                </motion.div>

                <h2 className="text-3xl font-bold mb-4 gold-gradient bg-clip-text text-transparent">
                  Thank You!
                </h2>

                <p className="text-gold-200 text-lg mb-2">
                  Your consultation request has been submitted successfully.
                </p>

                <p className="text-gold-300 mb-6">
                  We'll get back to you within 24 hours to schedule your consultation.
                </p>

                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate('/', { replace: true });
                    window.scrollTo(0, 0);
                  }}
                  className="gold-gradient-btn px-8 py-3 rounded-full text-black font-semibold hover:scale-105 transition-transform"
                >
                  Return to Home
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative min-h-screen bg-black text-white py-12 px-4 overflow-hidden">
        {/* Animated background orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-gold-500/10 blur-3xl"
            animate={{ x: [0, 80, 0], y: [0, 60, 0] }}
            transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute top-1/2 -right-40 w-[28rem] h-[28rem] rounded-full bg-brown-500/10 blur-3xl"
            animate={{ x: [0, -60, 0], y: [0, 80, 0] }}
            transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute -bottom-40 left-1/3 w-80 h-80 rounded-full bg-gold-400/5 blur-3xl"
            animate={{ x: [0, 50, 0], y: [0, -50, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <Helmet>
          <title>Book a Consultation | Terra Nexus AI</title>
          <meta name="description" content="Schedule a consultation with Terra Nexus AI. Let's discuss how our AI solutions can help your business grow." />
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href="https://terranexusai.com/contact" />
          <meta property="og:title" content="Book a Consultation | Terra Nexus AI" />
          <meta property="og:description" content="Schedule a consultation with Terra Nexus AI. Let's discuss how our AI solutions can help your business grow." />
          <meta property="og:url" content="https://terranexusai.com/contact" />
          <meta name="twitter:card" content="summary" />
        </Helmet>

        <div className="relative max-w-3xl mx-auto z-10">
          <Link to="/" className="inline-flex items-center text-gold-400 hover:text-gold-300 mb-8 transition-colors group">
            <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-black/50 backdrop-blur-md p-6 sm:p-8 rounded-3xl border border-gold-500/20 shadow-2xl"
          >
            {/* Header */}
            <div className="mb-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold-500/10 border border-gold-500/20 mb-4"
              >
                <Sparkles className="w-4 h-4 text-gold-400" />
                <span className="text-xs font-medium text-gold-300 tracking-wide uppercase">Consultation Request</span>
              </motion.div>
              <h1 className="text-3xl sm:text-4xl font-bold mb-2 gold-gradient bg-clip-text text-transparent">
                Schedule Your Consultation
              </h1>
              <p className="text-gold-200/80">
                Tell us about your business needs and let's explore how AI can help you grow.
              </p>
            </div>

            {/* Progress bar */}
            <div className="mb-10">
              <div className="flex items-center justify-between mb-4">
                {STEPS.map((step, idx) => {
                  const StepIcon = step.icon;
                  const isComplete = idx < currentStep || (idx === currentStep && stepCompletion(idx));
                  const isActive = idx === currentStep;
                  return (
                    <React.Fragment key={idx}>
                      <div className="flex flex-col items-center flex-1">
                        <motion.div
                          initial={false}
                          animate={{
                            scale: isActive ? 1.1 : 1,
                            backgroundColor: isComplete ? 'rgba(212, 175, 55, 0.2)' : 'rgba(0, 0, 0, 0.4)',
                            borderColor: isComplete ? 'rgba(212, 175, 55, 0.6)' : 'rgba(212, 175, 55, 0.2)',
                          }}
                          className={`w-11 h-11 rounded-full flex items-center justify-center border-2 relative ${isActive ? 'ring-4 ring-gold-500/20' : ''}`}
                        >
                          {isComplete && !isActive ? (
                            <Check className="w-5 h-5 text-gold-400" />
                          ) : (
                            <StepIcon className={`w-5 h-5 ${isActive ? 'text-gold-300' : 'text-gold-200/50'}`} />
                          )}
                        </motion.div>
                        <span className={`text-xs mt-2 font-medium hidden sm:block ${isActive ? 'text-gold-200' : isComplete ? 'text-gold-300/70' : 'text-gold-200/40'}`}>
                          {step.title}
                        </span>
                      </div>
                      {idx < STEPS.length - 1 && (
                        <div className="flex-1 h-0.5 mx-2 sm:mx-3 bg-gold-500/10 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: '0%' }}
                            animate={{ width: idx < currentStep ? '100%' : '0%' }}
                            transition={{ duration: 0.4 }}
                            className="h-full bg-gradient-to-r from-gold-400 to-brown-500"
                          />
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {submitError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mb-6 p-4 rounded-xl flex items-start gap-3 ${
                  submitError.type === 'network' ? 'bg-blue-500/10 text-blue-200 border border-blue-500/20' :
                  submitError.type === 'server' ? 'bg-red-500/10 text-red-200 border border-red-500/20' :
                  submitError.type === 'configuration' ? 'bg-amber-500/10 text-amber-200 border border-amber-500/20' :
                  'bg-yellow-500/10 text-yellow-200 border border-yellow-500/20'
                }`}
              >
                {submitError.type === 'network' ? <Wifi className="w-5 h-5 flex-shrink-0 mt-0.5" /> :
                 submitError.type === 'server' ? <ServerCrash className="w-5 h-5 flex-shrink-0 mt-0.5" /> :
                 <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                <div>
                  <p className="font-medium">{submitError.message}</p>
                  {submitError.details && (
                    <p className="text-sm mt-1 opacity-80">{submitError.details}</p>
                  )}
                </div>
              </motion.div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="relative overflow-hidden">
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div
                    key={currentStep}
                    custom={direction}
                    variants={stepVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                  >
                    {/* Step 0: About You */}
                    {currentStep === 0 && (
                      <div className="space-y-6">
                        <div>
                          <label htmlFor="fullName" className="flex items-center gap-2 text-sm font-medium text-gold-200 mb-2">
                            <User className="w-4 h-4 text-gold-400" />
                            Full Name*
                          </label>
                          <input
                            type="text"
                            id="fullName"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            className={inputClass}
                            placeholder="Enter your full name"
                          />
                          {errors.fullName && (
                            <p className="mt-2 text-red-400 text-sm flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors.fullName}
                            </p>
                          )}
                        </div>

                        <div>
                          <label htmlFor="email" className="flex items-center gap-2 text-sm font-medium text-gold-200 mb-2">
                            <Mail className="w-4 h-4 text-gold-400" />
                            Email Address*
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className={inputClass}
                            placeholder="your@email.com"
                          />
                          {errors.email && (
                            <p className="mt-2 text-red-400 text-sm flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors.email}
                            </p>
                          )}
                        </div>

                        <div>
                          <label htmlFor="phone" className="flex items-center gap-2 text-sm font-medium text-gold-200 mb-2">
                            <Phone className="w-4 h-4 text-gold-400" />
                            Phone Number*
                          </label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className={inputClass}
                            placeholder="(555) 123-4567"
                            maxLength={14}
                          />
                          {errors.phone && (
                            <p className="mt-2 text-red-400 text-sm flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors.phone}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Step 1: Your Business */}
                    {currentStep === 1 && (
                      <div className="space-y-6">
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-gold-200 mb-3">
                            <Sparkles className="w-4 h-4 text-gold-400" />
                            Service of Interest*
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {SERVICES.map((svc) => {
                              const ServiceIcon = svc.icon;
                              const isSelected = formData.service === svc.value;
                              return (
                                <motion.button
                                  type="button"
                                  key={svc.value}
                                  onClick={() => setFormData(prev => ({ ...prev, service: svc.value as FormData['service'] }))}
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  className={`relative flex items-start gap-3 p-4 rounded-xl border text-left transition-all duration-300 ${
                                    isSelected
                                      ? 'border-gold-500/60 bg-gold-500/10 ring-2 ring-gold-500/20'
                                      : 'border-gold-500/20 bg-black/30 hover:border-gold-500/40 hover:bg-gold-500/5'
                                  }`}
                                >
                                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                                    isSelected ? 'bg-gold-500/20' : 'bg-gold-500/10'
                                  }`}>
                                    <ServiceIcon className={`w-5 h-5 ${isSelected ? 'text-gold-300' : 'text-gold-400/70'}`} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`font-medium text-sm ${isSelected ? 'text-gold-200' : 'text-white/90'}`}>
                                      {svc.label}
                                    </p>
                                    <p className="text-xs text-gold-200/50 mt-0.5">{svc.desc}</p>
                                  </div>
                                  {isSelected && (
                                    <motion.div
                                      initial={{ scale: 0 }}
                                      animate={{ scale: 1 }}
                                      className="absolute top-3 right-3"
                                    >
                                      <Check className="w-4 h-4 text-gold-400" />
                                    </motion.div>
                                  )}
                                </motion.button>
                              );
                            })}
                          </div>
                        </div>

                        <div>
                          <label htmlFor="company" className="flex items-center gap-2 text-sm font-medium text-gold-200 mb-2">
                            <Building2 className="w-4 h-4 text-gold-400" />
                            Company Name
                          </label>
                          <input
                            type="text"
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className={inputClass}
                            placeholder="Your company name"
                          />
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label htmlFor="role" className="flex items-center gap-2 text-sm font-medium text-gold-200 mb-2">
                              <Briefcase className="w-4 h-4 text-gold-400" />
                              Your Role
                            </label>
                            <select
                              id="role"
                              name="role"
                              value={formData.role}
                              onChange={handleChange}
                              className={selectClass}
                              style={selectArrowStyle}
                            >
                              {ROLES.map(r => (
                                <option key={r.value} value={r.value} className="bg-black text-gold-200">{r.label}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label htmlFor="website" className="flex items-center gap-2 text-sm font-medium text-gold-200 mb-2">
                              <Globe className="w-4 h-4 text-gold-400" />
                              Website URL
                            </label>
                            <input
                              type="url"
                              id="website"
                              name="website"
                              value={formData.website}
                              onChange={handleChange}
                              className={inputClass}
                              placeholder="https://yourwebsite.com"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 2: Project Details */}
                    {currentStep === 2 && (
                      <div className="space-y-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label htmlFor="projectTimeline" className="flex items-center gap-2 text-sm font-medium text-gold-200 mb-2">
                              <Clock className="w-4 h-4 text-gold-400" />
                              Project Timeline
                            </label>
                            <select
                              id="projectTimeline"
                              name="projectTimeline"
                              value={formData.projectTimeline}
                              onChange={handleChange}
                              className={selectClass}
                              style={selectArrowStyle}
                            >
                              {TIMELINES.map(t => (
                                <option key={t.value} value={t.value} className="bg-black text-gold-200">{t.label}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label htmlFor="referralSource" className="flex items-center gap-2 text-sm font-medium text-gold-200 mb-2">
                              <HelpCircle className="w-4 h-4 text-gold-400" />
                              How Did You Hear About Us?
                            </label>
                            <select
                              id="referralSource"
                              name="referralSource"
                              value={formData.referralSource}
                              onChange={handleChange}
                              className={selectClass}
                              style={selectArrowStyle}
                            >
                              {SOURCES.map(s => (
                                <option key={s.value} value={s.value} className="bg-black text-gold-200">{s.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div>
                          <label htmlFor="problems" className="flex items-center gap-2 text-sm font-medium text-gold-200 mb-2">
                            <MessageSquare className="w-4 h-4 text-gold-400" />
                            What Problems Are You Looking to Solve?*
                          </label>
                          <textarea
                            id="problems"
                            name="problems"
                            value={formData.problems}
                            onChange={handleChange}
                            rows={4}
                            className={inputClass}
                            placeholder="Please describe the challenges you're facing and how you think AI could help"
                          />
                          {errors.problems && (
                            <p className="mt-2 text-red-400 text-sm flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors.problems}
                            </p>
                          )}
                        </div>

                        <div>
                          <label htmlFor="additionalInfo" className="flex items-center gap-2 text-sm font-medium text-gold-200 mb-2">
                            <MessageSquare className="w-4 h-4 text-gold-400" />
                            Additional Information
                          </label>
                          <textarea
                            id="additionalInfo"
                            name="additionalInfo"
                            value={formData.additionalInfo}
                            onChange={handleChange}
                            rows={3}
                            className={inputClass}
                            placeholder="Any other details you'd like to share"
                          />
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation buttons */}
              <div className="flex items-center justify-between mt-8 gap-4">
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-gold-200 border border-gold-500/20 hover:border-gold-500/40 hover:bg-gold-500/5 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>

                {currentStep < STEPS.length - 1 ? (
                  <motion.button
                    type="button"
                    onClick={handleNext}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-xl gold-gradient-btn text-black font-semibold"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4" />
                  </motion.button>
                ) : (
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: isSubmitting ? 1 : 1.03 }}
                    whileTap={{ scale: isSubmitting ? 1 : 0.97 }}
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-xl gold-gradient-btn text-black font-semibold disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full"
                        />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Request
                        <CheckCircle className="w-5 h-5" />
                      </>
                    )}
                  </motion.button>
                )}
              </div>

              <p className="text-sm text-gold-200/60 text-center mt-6">
                By submitting this form, you agree to our privacy policy and consent to being contacted about our services.
                We respect your privacy and will never share your information with third parties.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default ContactForm;
